import { z } from "zod";
import type { Currency } from "../@types/services/Currency";
import type { MeasureUnit } from "../@types/services/MeasureUnit";
import type { Proposal } from "../@types/services/Proposal";
import { api } from "./api";

interface ProposalLineData {
  gross_price: number;
  measure_unit: string | MeasureUnit;
  position: number;
  quantity: number;
  service_line_id?: string | null;
  short_text: string;
}
interface NewProposalData {
  commercialProposal: File;
  currency: string | Currency;
  lead_time: number;
  proposal_lines: ProposalLineData[];
  technicalProposal: File;
}

interface EditProposalData
  extends Omit<
    NewProposalData,
    "commercialProposal" | "lead_time" | "proposal_lines" | "technicalProposal"
  > {
  commercialProposal?: File;
  lead_time?: number;
  proposal_lines?: (ProposalLineData & { id?: string })[];
  technicalProposal?: File;
}

export type ProposalFields = {
  price: number;
};

const archivedProposalDocumentSchema = z.object({
  created_at: z.coerce.date(),
  document_type: z.enum(["commercial", "technical"]),
  filename: z.string(),
  id: z.uuid(),
  mail_user: z.string(),
});

export const archivedProposalDocumentsResponseSchema = z
  .union([
    z.array(archivedProposalDocumentSchema),
    z.object({ data: z.array(archivedProposalDocumentSchema) }),
  ])
  .transform((response) =>
    Array.isArray(response) ? response : response.data,
  );

export type ArchivedProposalDocument = z.infer<
  typeof archivedProposalDocumentSchema
>;

export const fetchProposal = async (id: string): Promise<Proposal> => {
  const response = await api.get<Proposal>(`/proposals/${id}`, {
    baseURL: "/vbuyer/api/v2",
  });

  return response.data;
};

export const getProposalDocument = async (
  id: string,
  documentType: string,
  documentId?: string,
): Promise<{
  url: string;
  filename: string;
}> => {
  const response = await api.get<{
    url: string;
    filename: string;
  }>("proposals/get_proposal_document", {
    baseURL: "/vbuyer/api/v2",
    params: {
      document_id: documentId,
      document_type: documentType,
      id,
    },
  });

  return response.data;
};

export const getArchivedProposalDocuments = async (
  proposalId: string,
): Promise<ArchivedProposalDocument[]> => {
  const response = await api.get<unknown>(
    "proposals/get_archived_proposal_documents",
    {
      baseURL: "/vbuyer/api/v2",
      params: {
        id: proposalId,
      },
    },
  );

  const result = archivedProposalDocumentsResponseSchema.safeParse(
    response.data,
  );
  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `${issue.path.join(".") || "<root>"}: ${issue.message}`)
      .join("; ");
    const responseKeys =
      response.data !== null &&
      typeof response.data === "object" &&
      !Array.isArray(response.data)
        ? Object.keys(response.data).join(", ")
        : "";
    const keysDetail = responseKeys ? `; response keys: ${responseKeys}` : "";
    throw new Error(
      `Invalid archived proposal documents response: ${issues}${keysDetail}`,
    );
  }

  return result.data;
};

const addOrUpdateProposalAttachments = async (
  id: string,
  technicalProposal: File | undefined,
  commercialProposal: File | undefined,
): Promise<void> => {
  const formData = new FormData();
  if (technicalProposal) {
    formData.append("technical_proposal", technicalProposal);
  }
  if (commercialProposal) {
    formData.append("commercial_proposal", commercialProposal);
  }
  await api.postForm<Proposal>(
    `/proposals/${id}/add_attachments_proposal`,
    formData,
    {
      baseURL: "/vbuyer/api/v2",
    },
  );
};

export const editProposal = async (
  proposal: EditProposalData,
  proposalId: string,
): Promise<Proposal> => {
  const response = await api.putForm<Proposal>(
    `/proposals/${proposalId}`,
    {
      proposal: {
        currency_id:
          typeof proposal.currency === "string"
            ? proposal.currency
            : proposal.currency.id,
        ...(proposal.lead_time != null
          ? { lead_time: proposal.lead_time }
          : {}),
        ...(proposal.proposal_lines !== undefined
          ? {
              proposal_lines_attributes: proposal.proposal_lines.map((pl) => ({
                gross_price: pl.gross_price,
                id: pl.id,
                measure_unit_id:
                  typeof pl.measure_unit === "string"
                    ? pl.measure_unit
                    : pl.measure_unit.id,
                position: pl.position,
                quantity: pl.quantity,
                service_line_id: pl.service_line_id ?? null,
                short_text: pl.short_text?.trim(),
              })),
            }
          : {}),
      },
    },
    {
      baseURL: "/vbuyer/api/v2",
    },
  );

  if (proposal.technicalProposal || proposal.commercialProposal) {
    await addOrUpdateProposalAttachments(
      response.data.id,
      proposal.technicalProposal,
      proposal.commercialProposal,
    );
  }

  return response.data;
};

export const createProposal = async (
  proposal: NewProposalData,
  serviceNegotiationId: string,
): Promise<Proposal> => {
  if (proposal.technicalProposal && proposal.commercialProposal) {
    const response = await api.post<Proposal>(
      "/proposals",
      {
        proposal: {
          currency_id:
            typeof proposal.currency === "string"
              ? proposal.currency
              : proposal.currency.id,
          lead_time: proposal.lead_time,
          proposal_lines_attributes: proposal.proposal_lines.map((pl) => ({
            gross_price: pl.gross_price,
            measure_unit_id:
              typeof pl.measure_unit === "string"
                ? pl.measure_unit
                : pl.measure_unit.id,
            position: pl.position,
            quantity: pl.quantity,
            service_line_id: pl.service_line_id ?? null,
            short_text: pl.short_text?.trim(),
          })),
          service_negotiation_id: serviceNegotiationId,
        },
      },
      {
        baseURL: "/vbuyer/api/v2",
      },
    );

    await addOrUpdateProposalAttachments(
      response.data.id,
      proposal.technicalProposal,
      proposal.commercialProposal,
    );

    return response.data;
  }
  throw new Error("Technical and commercial proposals are required.");
};
